/**
 * `/api/compare` — one point, every model, the official forecast and the
 * observed trace, on one axis in one set of units.
 *
 * One request rather than seven, because the NWS half has to be server-side
 * anyway and a phone on cellular should ask once. Everything a field *is* —
 * its label, units, decimals and window — comes back with it, from the same
 * registry that coloured the model plates, so nothing here restates any of it.
 */

export interface ComparePoint {
  /** An absolute instant, ISO with a `Z`. Not a lead: no two models share one. */
  t: string
  v: number | null
  /** True for "the model says there is nothing there" — at the sentinel or
   *  under the floor. `v: null` without it means we have no idea. */
  below?: boolean
  /** The span this value covers, in minutes, when it is not an instant. NWS
   *  QPF is 6-hourly on every entry and must never be drawn as a line. */
  w?: number
}

export type SeriesKind = 'model' | 'official' | 'observed'

export interface CompareSeries {
  kind: SeriesKind
  id: string
  label: string
  points: ComparePoint[]
  window_minutes: number
  /** Model series only. */
  run?: string
  run_age_minutes?: number
  complete?: boolean
  /** The official series only: when the office last updated the grid. */
  updated?: string | null
}

export interface CompareUnavailable {
  id: string
  /** A sentence, not a code. A missing line and a model nobody ingested look
   *  identical without one. */
  reason: string
}

export interface ComparePlace {
  city: string | null
  state: string | null
  /** How far the *named* place is from the point, which in open country is
   *  routinely twenty miles. */
  distance_mi: number | null
}

export interface CompareStation {
  id: string
  name: string | null
  distance_mi: number | null
}

export interface CompareField {
  id: string
  label: string
  units: string
  decimals: number
  window_minutes: number
}

export interface CompareBody {
  point: {
    lat: number
    lon: number
    /** The point's zone, which the chart's x axis is drawn in. */
    timezone: string | null
    place: ComparePlace | null
    station: CompareStation | null
  }
  field: CompareField
  generated: string
  series: CompareSeries[]
  unavailable: CompareUnavailable[]
}

/**
 * How far ahead the request asks for.
 *
 * Fixed, and wider than any window the picker offers, so changing the lead
 * window never costs a request: the panel filters what it already has. Eight
 * days is the NWS gridpoint product's own horizon, and the model half is not
 * bounded by this at all — a sixteen-day GFS comes back whole, because that
 * long tail is what a chart is for and what a map slider is not.
 */
export const COMPARE_HOURS = 192

// One request in flight at a time, the same rule `fetchForecast` follows: a
// slow answer for an old point must not land after a fast one for a new point
// and overwrite it.
let inFlight: AbortController | null = null

export async function fetchCompare(
  lat: number,
  lon: number,
  field: string,
  options: { hours?: number; models?: string[] } = {},
): Promise<CompareBody> {
  inFlight?.abort()
  const controller = new AbortController()
  inFlight = controller

  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    field,
    hours: String(options.hours ?? COMPARE_HOURS),
  })
  if (options.models?.length) params.set('models', options.models.join(','))

  try {
    const resp = await fetch(`/api/compare?${params}`, {
      signal: controller.signal,
    })
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`
      try {
        const body = await resp.json()
        if (body && typeof body.detail === 'string') detail = body.detail
      } catch {
        // Not JSON (a proxy error page). The status will do.
      }
      throw new Error(detail)
    }
    const parsed = await resp.json()
    // A shape check rather than a cast. A proxy, a stale service worker or a
    // dev server misrouting `/api` all answer 200 with *something*, and a
    // component that trusts the cast crashes on `body.field.label` — which
    // looks like a bug in the chart rather than in the response.
    if (!parsed || !Array.isArray(parsed.series) || typeof parsed.field?.units !== 'string') {
      throw new Error('/api/compare returned something that is not a comparison')
    }
    return parsed as CompareBody
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/** Test seam: the module-level controller outlives a component. */
export function _resetCompareInFlight(): void {
  inFlight = null
}
